import {CheckCircleIcon} from '@heroicons/react/solid';
import React, {useState} from 'react';

import {Button, FilePicker, Modal, MultiSelect} from '@/components';
import type {BusinessRFI} from '@/types';

import {getOutstandingDocuments} from './rfiStatus';

type UploadRFIDocModalProps = {
  rfi: BusinessRFI;
  isOpen: boolean;
  isLoading: boolean;
  dataCy?: string;
  onClose: () => void;
  onSubmit: (entries: {documentType: string; file: File}[]) => void;
};

const UploadRFIDocModal = ({
  rfi,
  isOpen,
  isLoading,
  dataCy,
  onClose,
  onSubmit,
}: UploadRFIDocModalProps) => {
  // Constrain to outstanding (fallback to all requested) so the uploaded key matches a request.
  const outstanding = getOutstandingDocuments(rfi);
  const choices = outstanding.length
    ? outstanding
    : (rfi.requestedDocuments ?? []);

  // `selected` is the comma-joined MultiSelect value; `files` holds a staged file per type.
  const [selected, setSelected] = useState<string>('');
  const [files, setFiles] = useState<Record<string, File>>({});

  const selectedTypes = selected.split(',').filter(Boolean);
  // The next type still awaiting a file — its upload component is the only one shown.
  const activeType = selectedTypes.find((type) => !files[type]);
  const allStaged =
    selectedTypes.length > 0 && selectedTypes.every((type) => files[type]);

  const handleSelectChange = (value: string) => {
    setSelected(value);
    // Drop staged files for any type that was de-selected.
    const types = value.split(',').filter(Boolean);
    setFiles((prev) =>
      Object.fromEntries(
        Object.entries(prev).filter(([type]) => types.includes(type)),
      ),
    );
  };

  const stageFile = (type: string, file: File | null) => {
    if (!file) {
      return;
    }
    setFiles((prev) => ({...prev, [type]: file}));
  };

  const unstageFile = (type: string) =>
    setFiles((prev) => {
      const next = {...prev};
      delete next[type];
      return next;
    });

  const handleSubmit = () => {
    if (!allStaged) {
      return;
    }
    onSubmit(
      selectedTypes.map((documentType) => ({
        documentType,
        file: files[documentType],
      })),
    );
  };

  return (
    <Modal
      dataCy={dataCy ? `${dataCy}-upload-documents` : undefined}
      isOpen={isOpen}
      onClose={onClose}
      title="Upload Documents"
      width="w-10/12 sm:w-10/12 md:w-7/12 lg:w-4/12"
      height="min-h-[18rem]"
      closeOnOutsideClick
      footer={
        <div className="flex justify-end gap-3">
          <Button
            dataCy={dataCy ? `${dataCy}-upload-documents-cancel` : undefined}
            type="button"
            variant="ghost"
            colorScheme="cyan"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            type="button"
            dataCy={dataCy ? `${dataCy}-upload-documents-confirm` : undefined}
            colorScheme="cyan"
            onClick={handleSubmit}
            isLoading={isLoading}
            loadingText="Uploading..."
            disabled={!allStaged}
          >
            Upload
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-100 mb-1">
            Documents to upload
          </label>
          <MultiSelect
            dataCy={dataCy ? `${dataCy}-upload-documents` : undefined}
            id="rfiUploadDocs"
            name="rfiUploadDocs"
            options={choices.map((doc) => ({value: doc, label: doc}))}
            value={selected}
            onChange={handleSelectChange}
            placeholder="--Select document(s)--"
          />
        </div>

        {/* Completed uploads (collapsed) */}
        {selectedTypes
          .filter((type) => files[type])
          .map((type) => (
            <div
              key={type}
              className="flex items-center justify-between gap-2 rounded-md border border-gray-200 dark:border-dark-borderMid px-3 py-2"
            >
              <span className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-100 min-w-0">
                <CheckCircleIcon className="w-4 h-4 text-successful-500 shrink-0" />
                <strong className="shrink-0">{type}</strong>
                <span className="text-gray-500 dark:text-gray-400 truncate min-w-0">
                  {files[type].name}
                </span>
              </span>
              <Button
                type="button"
                variant="ghost"
                colorScheme="cyan"
                className="!py-1 !px-2 shrink-0"
                onClick={() => unstageFile(type)}
              >
                <span className="text-xs">Change</span>
              </Button>
            </div>
          ))}

        {/* One active upload at a time. `key` per type forces a fresh FilePicker
            instance so its internal file state resets when we advance to the next
            document (otherwise the box stays "filled" with the previous file). */}
        {activeType && (
          <FilePicker
            key={activeType}
            name={`rfiDocument-${activeType}`}
            dataCy={dataCy ? `${dataCy}-upload-documents-file` : undefined}
            label={`Upload "${activeType}" (PDF, PNG, JPG)`}
            accept="application/pdf,image/png,image/jpeg"
            isLoading={false}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              stageFile(activeType, e.target.files?.[0] ?? null)
            }
          />
        )}
      </div>
    </Modal>
  );
};

export default UploadRFIDocModal;
